import { useState } from "react";
import { addEvent } from "@/lib/dppMutations";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dropzone } from "@/components/ui/dropzone";

const EVENT_TYPES: { value: string; label: string }[] = [
  { value: "service", label: "Service" },
  { value: "auction", label: "Auction" },
  { value: "initialSale", label: "Initial sale" },
  { value: "warranty", label: "Warranty" },
  { value: "resell", label: "Resell" },
  { value: "repair", label: "Repair" },
  { value: "refurbished", label: "Refurbished" },
  { value: "experience", label: "Experience" },
  { value: "transfer", label: "Transfer" },
  { value: "delivery", label: "Delivery" },
];

interface AddEventFormProps {
  brand: string;
  model: string;
  serial: string;
  onSuccess: () => void;
}

export function AddEventForm({
  brand,
  model,
  serial,
  onSuccess,
}: AddEventFormProps) {
  const [type, setType] = useState("service");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventImages, setEventImages] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await addEvent(
        brand,
        model,
        serial,
        { type, title, description, eventDate },
        eventImages,
        documents
      );
      setSubmitted(true);
      onSuccess();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to add event"));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
        Event submitted. It will appear on the passport shortly.
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
      >
        <div className="field">
          <Label htmlFor="event-type">Type</Label>
          <select
            id="event-type"
            className="select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={submitting}
            style={{ padding: "10px 14px", fontSize: 13, appearance: "none" }}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <Label htmlFor="event-date">Date</Label>
          <Input
            id="event-date"
            type="date"
            required
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="field">
        <Label htmlFor="event-title">Title</Label>
        <Input
          id="event-title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="field">
        <Label htmlFor="event-desc">Description</Label>
        <Input
          id="event-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="field">
        <Label>Images</Label>
        <Dropzone
          accept="image/*"
          variant="image"
          disabled={submitting}
          files={eventImages}
          onFiles={setEventImages}
          hint="PNG, JPG or HEIC"
        />
      </div>
      <div className="field">
        <Label>Documents (PDF)</Label>
        <Dropzone
          accept="application/pdf,.pdf"
          variant="document"
          disabled={submitting}
          files={documents}
          onFiles={setDocuments}
          hint="PDF only"
        />
      </div>

      {error && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? "Submitting…" : "Add event"}
      </Button>
    </form>
  );
}
